// Drift — the weekly sweep for work nobody is pushing (Isaac 2026-08-13).
//
// Standup answers "what is happening today". This tab answers the question
// nothing else on the page did: what stopped happening, and nobody noticed.
// Blocked, stale and unassigned work across every open project, in one list.
//
// The three buckets OVERLAP on purpose. A ticket that is blocked AND untouched
// for a month AND has no assignee is the worst row on the board, so it is ONE
// row carrying three badges — not three rows in three tables. The counts in the
// filter therefore sum to more than the row total, which is why each chip says
// what it counts rather than pretending to be a share of a whole.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { brainTokens } from "../../design/brainTheme";
import { Icon } from "../../design/ui";
import { EmptyState, PersonAvatar, Segmented, SegmentedMulti } from "../../mui";
import { getDrift, type DriftReason, type DriftResult, type DriftRow } from "../../lib/brain-api";

const c = brainTokens.color;

/** Tint per reason. Blocked is the loud one; unassigned is a gap, not a fire. */
const REASON_LOOK: Record<DriftReason, { label: string; fg: string; bg: string; border: string }> = {
  blocked: { label: "Blocked", fg: c.dangerText, bg: c.dangerBg, border: c.dangerBorder },
  stale: { label: "Stale", fg: c.warnText, bg: c.warnBg2, border: c.warnBorder },
  unassigned: { label: "Unassigned", fg: c.muted, bg: c.neutralPillBg, border: c.border },
};

const STALE_CHOICES = [
  { key: "7", label: "7 days" },
  { key: "14", label: "14 days" },
  { key: "30", label: "30 days" },
];

function ReasonBadge({ reason }: { reason: DriftReason }) {
  const look = REASON_LOOK[reason];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        fontSize: 11,
        fontWeight: 650,
        lineHeight: 1.5,
        px: "7px",
        borderRadius: `${brainTokens.radius.pill}px`,
        color: look.fg,
        background: look.bg,
        border: `1px solid ${look.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {look.label}
    </Box>
  );
}

/** `41d` — or an em-dash when the row has no timestamp we can trust. */
function IdleCell({ days, threshold }: { days: number | null; threshold: number }) {
  if (days === null) {
    return (
      <Box component="span" sx={{ color: c.placeholder }} title="No timestamp on this row">
        —
      </Box>
    );
  }
  const hot = days >= threshold * 2;
  return (
    <Box
      component="span"
      sx={{
        fontFamily: brainTokens.font.mono,
        fontSize: 12.5,
        fontWeight: hot ? 700 : 500,
        color: hot ? c.dangerText : days >= threshold ? c.warnText : c.muted,
      }}
    >
      {days}d
    </Box>
  );
}

export function DriftTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<DriftResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [staleDays, setStaleDays] = useState("14");
  const [reasons, setReasons] = useState<DriftReason[]>([]);

  const load = useCallback(
    async (days: string) => {
      setLoading(true);
      try {
        setData(await getDrift(Number(days)));
        setError(null);
      } catch (e) {
        // A failed sweep must NEVER fall through to the "nothing is drifting"
        // empty state. Silence and all-clear look identical on a dashboard
        // whose entire job is catching what nobody is watching.
        const msg = e instanceof Error ? e.message : "Failed to sweep for drifting work";
        enqueueSnackbar(msg, { variant: "error" });
        setData(null);
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    void load(staleDays);
  }, [load, staleDays]);

  const rows = data?.rows ?? [];
  // Empty selection = everything, same convention as the register's status chips.
  const shown = useMemo(
    () => (reasons.length ? rows.filter((r) => r.reasons.some((x) => reasons.includes(x))) : rows),
    [rows, reasons],
  );

  const counts = data?.counts;
  const threshold = data?.staleDays ?? Number(staleDays);

  const filterOptions = useMemo(
    () => [
      { key: "all" as const, label: `All (${counts?.rows ?? 0})` },
      { key: "blocked" as const, label: `Blocked (${counts?.blocked ?? 0})` },
      { key: "stale" as const, label: `Stale (${counts?.stale ?? 0})` },
      { key: "unassigned" as const, label: `Unassigned (${counts?.unassigned ?? 0})` },
    ],
    [counts],
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <SegmentedMulti<DriftReason | "all">
          value={reasons}
          onChange={(v) => setReasons(v.filter((x): x is DriftReason => x !== "all"))}
          options={filterOptions}
          clearKey="all"
        />
        <Box sx={{ flex: "1 1 auto" }} />
        <Box sx={{ fontSize: 12, color: c.faint }}>Untouched for</Box>
        <Segmented value={staleDays} onChange={setStaleDays} options={STALE_CHOICES} />
        <Button
          size="small"
          variant="outlined"
          onClick={() => void load(staleDays)}
          disabled={loading}
          startIcon={<Icon name="refresh" size={15} />}
        >
          Refresh
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={26} />
        </Box>
      ) : error ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            p: "16px 18px",
            border: `1px solid ${c.dangerBorder}`,
            background: c.dangerBg2,
            borderRadius: `${brainTokens.radius.card}px`,
          }}
        >
          <Box>
            <Box sx={{ fontSize: 14.5, fontWeight: 600, color: c.ink }}>
              Couldn&apos;t sweep for drifting work.
            </Box>
            <Box sx={{ fontSize: 12.5, color: c.muted, mt: 0.25 }}>
              This is a failure, not an all-clear — nothing here has been checked. {error}
            </Box>
          </Box>
          <Button size="small" variant="outlined" onClick={() => void load(staleDays)}>
            Retry
          </Button>
        </Box>
      ) : !rows.length ? (
        <EmptyState icon="circle-check" title="Nothing is drifting.">
          No blocked, stale or unassigned work across any open project. Enjoy it.
        </EmptyState>
      ) : !shown.length ? (
        <EmptyState icon="filter" title="No rows match that filter.">
          {counts?.rows ?? 0} rows are drifting overall — clear the filter to see them.
        </EmptyState>
      ) : (
        <Box sx={{ border: `1px solid ${c.border}`, borderRadius: `${brainTokens.radius.card}px`, overflow: "hidden" }}>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 210 }}>Why</TableCell>
                  <TableCell sx={{ width: 108 }}>Ref</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell sx={{ width: 170 }}>Project</TableCell>
                  <TableCell sx={{ width: 140 }}>Status</TableCell>
                  <TableCell sx={{ width: 150 }}>Assignee</TableCell>
                  <TableCell sx={{ width: 70 }} align="right">Idle</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((row) => (
                  <DriftTableRow key={`${row.source}:${row.projectId}:${row.ref}`} row={row} threshold={threshold} />
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}

      {data && !loading && !error && (
        <Box sx={{ mt: 1.5, fontSize: 11.5, color: c.faint }}>
          {shown.length} of {rows.length} shown · stale means untouched for {threshold}+ days · a
          row can be counted in more than one bucket
        </Box>
      )}
    </Box>
  );
}

function DriftTableRow({ row, threshold }: { row: DriftRow; threshold: number }) {
  return (
    <TableRow hover>
      <TableCell>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {row.reasons.map((r) => (
            <ReasonBadge key={r} reason={r} />
          ))}
        </Box>
      </TableCell>
      <TableCell>
        {row.url ? (
          <Box
            component="a"
            href={row.url}
            target="_blank"
            rel="noopener"
            sx={{
              fontFamily: brainTokens.font.mono,
              fontSize: 12,
              color: c.tealInk,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {row.ref}
          </Box>
        ) : (
          <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontSize: 12, color: c.muted2 }}>
            {row.source === "action" ? "brain" : row.ref}
          </Box>
        )}
      </TableCell>
      <TableCell sx={{ color: c.ink2 }}>{row.title}</TableCell>
      <TableCell>
        <Box
          component={RouterLink}
          to={`/projects/${encodeURIComponent(row.projectId)}`}
          sx={{ fontSize: 12.5, color: c.tealInk, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
        >
          {row.projectName}
        </Box>
      </TableCell>
      <TableCell sx={{ fontSize: 12.5, color: c.muted }}>{row.status || "—"}</TableCell>
      <TableCell>
        {row.assignee ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
            <PersonAvatar name={row.assignee} size={22} />
            <Box sx={{ fontSize: 12.5, color: c.ink3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {row.assignee}
            </Box>
          </Box>
        ) : (
          <Box component="span" sx={{ fontSize: 12.5, color: c.placeholder }}>Nobody</Box>
        )}
      </TableCell>
      <TableCell align="right">
        <IdleCell days={row.idleDays} threshold={threshold} />
      </TableCell>
    </TableRow>
  );
}
